/**
 * Leviathan Web - Bill of Lading Print Page
 * Quasar Prime LLC
 *
 * Print-optimized BOL document with standard layout
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Printer } from 'lucide-react';

import { API_URL } from '@/config/api';

interface BOLItem {
  id: string;
  productName: string;
  productCode: string | null;
  orderedQuantity: number;
  loadedQuantity: number;
  deliveredQuantity: number | null;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  tankNumber: string | null;
  compartmentNumber: number | null;
}

interface PrintData {
  bol: {
    id: string;
    bolNumber: string;
    status: string;
    customerName: string;
    customerAddress: string | null;
    customerCity: string | null;
    customerState: string | null;
    customerZip: string | null;
    deliveryAddress: string;
    deliveryCity: string;
    deliveryState: string;
    deliveryZip: string;
    deliveryInstructions: string | null;
    driverName: string | null;
    driverLicense: string | null;
    truckNumber: string | null;
    trailerNumber: string | null;
    loadingDate: string | null;
    loadingTime: string | null;
    loadedBy: string | null;
    sourceTerminal: string | null;
    sealNumber: string | null;
    deliveryDate: string | null;
    deliveryTime: string | null;
    receivedBy: string | null;
    receivedTitle: string | null;
    driverSignature: string | null;
    driverSignedAt: string | null;
    customerSignature: string | null;
    customerSignedAt: string | null;
    startMeterReading: number | null;
    endMeterReading: number | null;
    grossQuantity: number | null;
    netQuantity: number | null;
    totalQuantity: number;
    totalAmount: number;
    notes: string | null;
    createdAt: string;
    items: BOLItem[];
    order?: { orderNumber: string };
  };
  company: {
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    fax: string | null;
    logo: string | null;
  };
}

/**
 * BOL Print Page Component
 */
const BOLPrintPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const [printData, setPrintData] = useState<PrintData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchPrintData();
    }
  }, [id]);

  const fetchPrintData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bol/${id}/print`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPrintData(data);
        // Mark as printed
        await fetch(`${API_URL}/api/bol/${id}/printed`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      }
    } catch (error) {
      console.error('Failed to fetch print data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatQuantity = (qty: number) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(qty);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading BOL...</div>
      </div>
    );
  }

  if (!printData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">BOL not found</div>
      </div>
    );
  }

  const { bol, company } = printData;

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden bg-panel-dark border-b border-panel-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link
            to={`/bol/${id}`}
            className="flex items-center space-x-2 text-text-secondary hover:text-amber transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-mono text-sm">Back to BOL</span>
          </Link>
          <button
            onClick={handlePrint}
            className="flex items-center space-x-2 bg-amber text-background px-4 py-2 font-mono text-sm uppercase hover:bg-amber/80 transition-colors"
          >
            <Printer className="w-4 h-4" />
            <span>Print BOL</span>
          </button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="bg-white min-h-screen print:min-h-0">
        <div className="max-w-4xl mx-auto p-8 print:p-0 print:max-w-none">
          {/* BOL Document */}
          <div className="bg-white text-black print:bg-white">
            {/* Header */}
            <div className="border-b-2 border-black pb-4 mb-4">
              <div className="flex justify-between items-start">
                <div>
                  {company.logo ? (
                    <img src={company.logo} alt={company.name} className="h-12 mb-2" />
                  ) : (
                    <div className="text-2xl font-bold">{company.name}</div>
                  )}
                  {company.address && (
                    <div className="text-sm">
                      {company.address}<br />
                      {company.city}, {company.state} {company.zip}
                    </div>
                  )}
                  {company.phone && <div className="text-sm">Tel: {company.phone}</div>}
                  {company.fax && <div className="text-sm">Fax: {company.fax}</div>}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">BILL OF LADING</div>
                  <div className="text-xl font-mono mt-2">{bol.bolNumber}</div>
                  <div className="text-sm mt-1">Date: {formatDate(bol.createdAt)}</div>
                  {bol.order && (
                    <div className="text-sm">Order: {bol.order.orderNumber}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Ship To / Sold To */}
            <div className="grid grid-cols-2 gap-8 mb-6">
              <div className="border border-black p-3">
                <div className="font-bold text-sm mb-2 bg-gray-100 -m-3 mb-2 p-2">SOLD TO:</div>
                <div className="font-bold">{bol.customerName}</div>
                {bol.customerAddress && (
                  <div className="text-sm">
                    {bol.customerAddress}<br />
                    {bol.customerCity}, {bol.customerState} {bol.customerZip}
                  </div>
                )}
              </div>
              <div className="border border-black p-3">
                <div className="font-bold text-sm mb-2 bg-gray-100 -m-3 mb-2 p-2">SHIP TO:</div>
                <div className="text-sm">
                  {bol.deliveryAddress}<br />
                  {bol.deliveryCity}, {bol.deliveryState} {bol.deliveryZip}
                </div>
                {bol.deliveryInstructions && (
                  <div className="text-xs mt-2 italic">
                    Instructions: {bol.deliveryInstructions}
                  </div>
                )}
              </div>
            </div>

            {/* Carrier Info */}
            <div className="grid grid-cols-4 gap-4 mb-6 text-sm">
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Driver</div>
                <div className="font-mono">{bol.driverName || '-'}</div>
              </div>
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Truck #</div>
                <div className="font-mono">{bol.truckNumber || '-'}</div>
              </div>
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Trailer #</div>
                <div className="font-mono">{bol.trailerNumber || '-'}</div>
              </div>
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Seal #</div>
                <div className="font-mono">{bol.sealNumber || '-'}</div>
              </div>
            </div>

            {/* Loading Info */}
            <div className="grid grid-cols-3 gap-4 mb-6 text-sm">
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Loading Date</div>
                <div className="font-mono">{formatDate(bol.loadingDate)} {bol.loadingTime || ''}</div>
              </div>
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Source Terminal</div>
                <div className="font-mono">{bol.sourceTerminal || '-'}</div>
              </div>
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Loaded By</div>
                <div className="font-mono">{bol.loadedBy || '-'}</div>
              </div>
            </div>

            {/* Products Table */}
            <table className="w-full border-collapse mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-black p-2 text-left text-sm">Product</th>
                  <th className="border border-black p-2 text-center text-sm">Comp.</th>
                  <th className="border border-black p-2 text-right text-sm">Ordered</th>
                  <th className="border border-black p-2 text-right text-sm">Loaded</th>
                  <th className="border border-black p-2 text-right text-sm">Delivered</th>
                  <th className="border border-black p-2 text-right text-sm">Price</th>
                  <th className="border border-black p-2 text-right text-sm">Amount</th>
                </tr>
              </thead>
              <tbody>
                {bol.items.map((item) => (
                  <tr key={item.id}>
                    <td className="border border-black p-2">
                      <div className="font-bold">{item.productName}</div>
                      {item.productCode && <div className="text-xs text-gray-600">{item.productCode}</div>}
                    </td>
                    <td className="border border-black p-2 text-center font-mono">
                      {item.compartmentNumber || '-'}
                    </td>
                    <td className="border border-black p-2 text-right font-mono">
                      {formatQuantity(item.orderedQuantity)} {item.unit}
                    </td>
                    <td className="border border-black p-2 text-right font-mono">
                      {formatQuantity(item.loadedQuantity)} {item.unit}
                    </td>
                    <td className="border border-black p-2 text-right font-mono">
                      {item.deliveredQuantity !== null
                        ? `${formatQuantity(item.deliveredQuantity)} ${item.unit}`
                        : ''}
                    </td>
                    <td className="border border-black p-2 text-right font-mono">
                      {formatCurrency(item.unitPrice)}
                    </td>
                    <td className="border border-black p-2 text-right font-mono">
                      {formatCurrency(item.lineTotal)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50">
                  <td colSpan={4} className="border border-black p-2"></td>
                  <td className="border border-black p-2 text-right font-bold">
                    {formatQuantity(bol.totalQuantity)} gal
                  </td>
                  <td className="border border-black p-2 text-right font-bold">Total:</td>
                  <td className="border border-black p-2 text-right font-bold font-mono">
                    {formatCurrency(bol.totalAmount)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Meter Readings */}
            {(bol.startMeterReading !== null || bol.endMeterReading !== null) && (
              <div className="grid grid-cols-4 gap-4 mb-6 text-sm">
                <div className="border border-black p-2">
                  <div className="text-xs text-gray-600">Start Meter</div>
                  <div className="font-mono">{bol.startMeterReading !== null ? formatQuantity(bol.startMeterReading) : '-'}</div>
                </div>
                <div className="border border-black p-2">
                  <div className="text-xs text-gray-600">End Meter</div>
                  <div className="font-mono">{bol.endMeterReading !== null ? formatQuantity(bol.endMeterReading) : '-'}</div>
                </div>
                <div className="border border-black p-2">
                  <div className="text-xs text-gray-600">Gross Quantity</div>
                  <div className="font-mono">{bol.grossQuantity !== null ? `${formatQuantity(bol.grossQuantity)} gal` : '-'}</div>
                </div>
                <div className="border border-black p-2">
                  <div className="text-xs text-gray-600">Net Quantity</div>
                  <div className="font-mono font-bold">{bol.netQuantity !== null ? `${formatQuantity(bol.netQuantity)} gal` : '-'}</div>
                </div>
              </div>
            )}

            {/* Delivery Info */}
            <div className="grid grid-cols-3 gap-4 mb-6 text-sm">
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Delivery Date</div>
                <div className="font-mono">{formatDate(bol.deliveryDate)} {bol.deliveryTime || ''}</div>
              </div>
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Received By</div>
                <div className="font-mono">{bol.receivedBy || ''}</div>
              </div>
              <div className="border border-black p-2">
                <div className="text-xs text-gray-600">Title</div>
                <div className="font-mono">{bol.receivedTitle || ''}</div>
              </div>
            </div>

            {/* Notes */}
            {bol.notes && (
              <div className="border border-black p-3 mb-6">
                <div className="font-bold text-sm mb-1">Notes:</div>
                <div className="text-sm">{bol.notes}</div>
              </div>
            )}

            {/* Signatures */}
            <div className="grid grid-cols-2 gap-8 mt-8">
              <div className="border border-black p-4">
                <div className="text-sm font-bold mb-2">DRIVER SIGNATURE</div>
                {bol.driverSignature ? (
                  <div>
                    <img src={bol.driverSignature} alt="Driver Signature" className="h-16 mb-2" />
                    <div className="text-xs text-gray-600">
                      Signed: {formatDate(bol.driverSignedAt)}
                    </div>
                  </div>
                ) : (
                  <div className="h-16 border-b border-black"></div>
                )}
                <div className="mt-2 pt-2 border-t border-gray-300 text-sm">
                  <div>{bol.driverName || ''}</div>
                  {bol.driverLicense && <div className="text-xs">License: {bol.driverLicense}</div>}
                </div>
              </div>
              <div className="border border-black p-4">
                <div className="text-sm font-bold mb-2">CUSTOMER SIGNATURE</div>
                {bol.customerSignature ? (
                  <div>
                    <img src={bol.customerSignature} alt="Customer Signature" className="h-16 mb-2" />
                    <div className="text-xs text-gray-600">
                      Signed: {formatDate(bol.customerSignedAt)}
                    </div>
                  </div>
                ) : (
                  <div className="h-16 border-b border-black"></div>
                )}
                <div className="mt-2 pt-2 border-t border-gray-300 text-sm">
                  <div>{bol.receivedBy || ''}</div>
                  <div className="text-xs">{bol.receivedTitle || ''}</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-4 border-t border-gray-300 text-xs text-gray-600 text-center">
              <p>I hereby certify that the above named materials are properly classified, packaged, marked, and labeled,</p>
              <p>and are in proper condition for transportation according to the applicable regulations.</p>
              <p className="mt-2">Shipper: {company.name} | BOL: {bol.bolNumber}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.5in;
          }

          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default BOLPrintPage;
